import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Play, Headphones, Image as ImageIcon, Youtube } from 'lucide-react';
import { Button } from '../components/ui/button';
import { sermons, galleryImages } from '../mockData';

export const Media = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredGallery = selectedCategory === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === selectedCategory);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#800020] text-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-4">Media</h1>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
          <p className="text-xl text-[#fef3c7]">
            Watch sermons, explore our photo gallery, and join us online
          </p>
        </div>
      </section>

      {/* Sermon Archive */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">Recent Sermons</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-4"></div>
            <p className="text-lg text-gray-700">
              Catch up on messages you may have missed or revisit your favorites
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sermons.map((sermon) => (
              <Card key={sermon.id} className="overflow-hidden border-2 border-[#800020] hover:shadow-xl transition-shadow">
                <div className="relative">
                  <img 
                    src={sermon.thumbnail} 
                    alt={sermon.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <Button className="bg-[#D4AF37] hover:bg-[#B8860B] rounded-full w-16 h-16 p-0">
                      <Play className="h-8 w-8 text-white fill-white" />
                    </Button>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#800020] mb-2">{sermon.title}</h3>
                  <p className="text-[#D4AF37] font-semibold mb-2">{sermon.pastor}</p>
                  <p className="text-sm text-gray-600 mb-2">{sermon.scripture}</p>
                  <p className="text-sm text-gray-500 mb-4">{sermon.date}</p>
                  <div className="flex gap-3">
                    <Button variant="outline" className="flex-1 border-[#800020] text-[#800020] hover:bg-[#fffbeb]">
                      <Play className="h-4 w-4 mr-2" />
                      Watch
                    </Button>
                    <Button variant="outline" className="flex-1 border-[#800020] text-[#800020] hover:bg-[#fffbeb]">
                      <Headphones className="h-4 w-4 mr-2" />
                      Listen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button className="bg-[#D4AF37] hover:bg-[#B8860B] text-white px-8">
              View Full Sermon Archive
            </Button>
          </div>
        </div>
      </section>

      {/* Livestream */}
      <section className="py-16 bg-[#fffbeb]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">Watch Live</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-4"></div>
            <p className="text-lg text-gray-700">
              Join us for live worship services on 1st, 3rd, and 4th Sundays at 10:00 AM
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <Card className="bg-white border-2 border-[#800020]">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-[#fffbeb] rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="h-10 w-10 text-[#D4AF37]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-[#800020] mb-4">Facebook Live</h3>
                <p className="text-gray-700 mb-6">
                  Join our worship experience on Facebook Live. Follow our page to stay connected with our church family and receive notifications when we go live.
                </p>
                <Button className="bg-[#800020] hover:bg-[#8B0000] text-white w-full">
                  Watch on Facebook Live
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">Photo Gallery</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-4"></div>
            <p className="text-lg text-gray-700">
              Moments from our church life and ministry
            </p>
          </div>

          {/* Gallery Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              className={selectedCategory === 'all' 
                ? 'bg-[#D4AF37] hover:bg-[#B8860B] text-white' 
                : 'border-[#800020] text-[#800020] hover:bg-[#fffbeb]'
              }
              onClick={() => setSelectedCategory('all')}
            >
              All Photos
            </Button>
            <Button
              variant={selectedCategory === 'worship' ? 'default' : 'outline'}
              className={selectedCategory === 'worship' 
                ? 'bg-[#D4AF37] hover:bg-[#B8860B] text-white' 
                : 'border-[#800020] text-[#800020] hover:bg-[#fffbeb]'
              }
              onClick={() => setSelectedCategory('worship')}
            >
              Worship
            </Button>
            <Button
              variant={selectedCategory === 'fellowship' ? 'default' : 'outline'}
              className={selectedCategory === 'fellowship' 
                ? 'bg-[#D4AF37] hover:bg-[#B8860B] text-white' 
                : 'border-[#800020] text-[#800020] hover:bg-[#fffbeb]'
              }
              onClick={() => setSelectedCategory('fellowship')}
            >
              Fellowship
            </Button>
            <Button
              variant={selectedCategory === 'outreach' ? 'default' : 'outline'}
              className={selectedCategory === 'outreach' 
                ? 'bg-[#D4AF37] hover:bg-[#B8860B] text-white' 
                : 'border-[#800020] text-[#800020] hover:bg-[#fffbeb]'
              }
              onClick={() => setSelectedCategory('outreach')}
            >
              Outreach
            </Button>
          </div>

          {/* Gallery Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGallery.map((image) => (
              <div key={image.id} className="relative group overflow-hidden rounded-lg shadow-lg cursor-pointer">
                <img 
                  src={image.url} 
                  alt={image.caption}
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-semibold">{image.caption}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Podcast Section */}
      <section className="py-16 bg-[#800020] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-6">
            <Headphones className="h-10 w-10 text-white" />
          </div>
          <h2 className="text-4xl font-bold mb-4">Listen on the Go</h2>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
          <p className="text-xl text-[#fef3c7] mb-8">
            Subscribe to our podcast and never miss a sermon. Available on all major podcast platforms.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button className="bg-white text-[#800020] hover:bg-[#fffbeb]">
              Apple Podcasts
            </Button>
            <Button className="bg-white text-[#800020] hover:bg-[#fffbeb]">
              Spotify
            </Button>
            <Button className="bg-white text-[#800020] hover:bg-[#fffbeb]">
              Google Podcasts
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
